"use client";

import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import SatkomlekLogo from "@/app/image/satkomlekLogo.png";
import { usePermission } from "@/app/context/permissionContext";
import { PERMISSION, MODULE } from "@/app/permissions/permissions";

import {
  ChevronLeftCircle,
  ChevronRightCircle,
  LayoutDashboard,
  Boxes,
  BookOpen,
  History,
  BarChart3,
  Tags,
  Factory,
  Globe,
  Building2,
  Users,
  Settings,
  ClipboardList,
  ScanBarcode,
  UserRoundKey,
  FilePenIcon,
  Settings2,
  type LucideIcon,
  PlusCircle,
  Trash2,
  ArrowLeftRight,
  Undo2,
  Target,
} from "lucide-react";

type SidebarProps = {
  isMobile?: boolean;
  open?: boolean;
  onClose?: () => void;
};

/* =========================================================================
   KONFIGURASI MENU + HAK AKSES

   NOTE: MENU (dan tipe-tipenya) di-export supaya jadi SATU-SATUNYA sumber
   kebenaran untuk "menu mana yang bergantung pada privilege mana". File
   app/hooks/usePermissions.ts (buildMenuModul) men-derive baris tabel Hak
   Akses langsung dari sini.

   `privilege: null` = menu PUBLIK untuk semua pengguna yang sudah login:
   selalu tampil di sidebar dan TIDAK muncul di tabel Hak Akses
   (buildMenuModul melewati item tanpa privilege).
   ========================================================================= */
export type MenuItemConfig = {
  label: string;
  icon: LucideIcon;
  url: string;
  privilege?: number | null;
  permission?: number;
};
export type MenuGroup = { title: string; items: MenuItemConfig[] };

export const MENU: MenuGroup[] = [
  {
    title: "Menu Utama",
    items: [
      {
        label: "Dashboard",
        icon: LayoutDashboard,
        url: "/pages/menu-utama/dashboard",
        privilege: MODULE.Dashboard,
      },
      {
        label: "Koleksi Material",
        icon: Boxes,
        url: "/pages/menu-utama/koleksi-material",
        privilege: MODULE.Aset,
        permission: PERMISSION.READ,
      },
      {
        label: "Data Peminjaman",
        icon: Boxes,
        url: "/pages/menu-utama/data-peminjaman",
        privilege: MODULE.Aset,
        permission: PERMISSION.READ,
      },
      {
        label: "Usulan Kodefikasi",
        icon: PlusCircle,
        url: "/pages/menu-utama/usulan-kodefikasi",
        privilege: MODULE.MatMasuk,
        permission: PERMISSION.READ,
      },
      {
        label: "Usulan Perubahan",
        icon: FilePenIcon,
        url: "/pages/menu-utama/usulan-perubahan",
        privilege: MODULE.MatEdit,
        permission: PERMISSION.READ,
      },
      {
        label: "Usulan Penghapusan",
        icon: Trash2,
        url: "/pages/menu-utama/usulan-penghapusan",
        privilege: MODULE.MatKeluar,
        permission: PERMISSION.READ,
      },
      {
        label: "Proses Kodefikasi",
        icon: Settings2,
        url: "/pages/menu-utama/proses-kodefikasi",
        privilege: MODULE.MatMasuk,
        permission: PERMISSION.FULL,
      },
      {
        // Akses dibuka untuk SEMUA pengguna oleh backend — tidak diatur
        // lewat fitur Hak Akses, jadi tanpa privilege (selalu tampil).
        label: "Usulan Peminjaman",
        icon: ArrowLeftRight,
        url: "/pages/menu-utama/usulan-peminjaman",
        privilege: null,
      },
      {
        // Sama seperti Usulan Peminjaman: terbuka untuk semua pengguna.
        label: "Usulan Pengembalian",
        icon: Undo2,
        url: "/pages/menu-utama/usulan-pengembalian",
        privilege: null,
      },
      {
        label: "Referensi",
        icon: BookOpen,
        url: "/pages/menu-utama/referensi",
        privilege: MODULE.DocFile,
        permission: PERMISSION.READ,
      },
      {
        label: "Laporan",
        icon: BarChart3,
        url: "/pages/menu-utama/laporan",
        privilege: MODULE.Aset,
        permission: PERMISSION.READ,
      },
    ],
  },
  {
    title: "Operasional",
    items: [
      {
        label: "Barcode Scanner",
        icon: ScanBarcode,
        url: "/pages/operasional/barcode-scanner",
        privilege: MODULE.Aset,
        permission: PERMISSION.READ,
      },
    ],
  },
  {
    title: "Master Data",
    items: [
      {
        label: "Kategori Material",
        icon: Tags,
        url: "/pages/master-data/kategori-material",
        privilege: MODULE.Klasifikasi,
        permission: PERMISSION.READ,
      },
      {
        label: "Pabrikan",
        icon: Factory,
        url: "/pages/master-data/pabrikan",
        privilege: MODULE.Aset,
        permission: PERMISSION.READ,
      },
      {
        label: "Negara",
        icon: Globe,
        url: "/pages/master-data/negara",
        privilege: MODULE.Aset,
        permission: PERMISSION.READ,
      },
      {
        label: "Satuan Pemilik",
        icon: Building2,
        url: "/pages/master-data/satuan-pemilik",
        // /identifikasi/* is gated on MODULE.Identifikasi (split off from
        // Klasifikasi on the backend) — this page only calls identifikasi
        // endpoints (identifikasiMasterServices / identifikasiServices via
        // useMaps), never klasifikasi ones.
        privilege: MODULE.Identifikasi,
        permission: PERMISSION.READ,
      },
      {
        label: "Benchmark",
        icon: Target,
        url: "/pages/master-data/benchmark",
        privilege: MODULE.Benchmark,
        permission: PERMISSION.READ,
      },
    ],
  },
  {
    title: "Administrasi",
    items: [
      {
        label: "Riwayat Penghapusan",
        icon: History,
        url: "/pages/administrasi/riwayat-penghapusan",
        privilege: MODULE.MatKeluar,
        permission: PERMISSION.READ,
      },
      {
        label: "Pengguna",
        icon: Users,
        url: "/pages/administrasi/pengguna",
        privilege: MODULE.User,
        permission: PERMISSION.FULL,
      },
      {
        label: "Permissions",
        icon: UserRoundKey,
        url: "/pages/administrasi/permissions",
        privilege: MODULE.Privilege,
        permission: PERMISSION.FULL,
      },
      {
        label: "Pengaturan",
        icon: Settings,
        url: "/pages/administrasi/pengaturan",
        privilege: MODULE.Personel,
        permission: PERMISSION.READ,
      },
      {
        label: "Audit Trail",
        icon: ClipboardList,
        url: "/pages/administrasi/audit-trail",
        privilege: MODULE.AuditTrail,
        permission: PERMISSION.READ,
      },
    ],
  },
];

// Context untuk tooltip saat sidebar (mode rail) collapsed
type TipContextType = {
  collapsed: boolean;
  show: (label: string, rect: DOMRect) => void;
  hide: () => void;
};
const SidebarTipContext = createContext<TipContextType | null>(null);

/* =========================================================================
   DETEKSI MODE SHELL: phone (drawer) / tablet (bottom dock) / desktop (rail)
   ========================================================================= */
export type ShellMode = "phone" | "tablet" | "desktop";

const TABLET_MIN_WIDTH = 600;
const TABLET_MAX_WIDTH = 1440;
const TABLET_MIN_HEIGHT = 560;

function computeShellMode(): ShellMode {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  const h = window.innerHeight;
  const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;

  if (
    isCoarsePointer &&
    w >= TABLET_MIN_WIDTH &&
    w <= TABLET_MAX_WIDTH &&
    h >= TABLET_MIN_HEIGHT
  ) {
    return "tablet";
  }
  if (w < TABLET_MIN_WIDTH || (isCoarsePointer && h < TABLET_MIN_HEIGHT)) {
    return "phone";
  }
  return "desktop";
}

export function useShellMode(): ShellMode {
  const [mode, setMode] = useState<ShellMode>("desktop");

  useEffect(() => {
    const update = () => setMode(computeShellMode());
    update();

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    const pointerMql = window.matchMedia("(pointer: coarse)");
    pointerMql.addEventListener?.("change", update);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      pointerMql.removeEventListener?.("change", update);
    };
  }, []);

  return mode;
}

export default function Sidebar({
  isMobile = false,
  open = true,
  onClose,
}: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { can, loading } = usePermission();
  const shellMode = useShellMode();

  const [collapsed, setCollapsed] = useState(false);
  const [tip, setTip] = useState<{
    label: string;
    top: number;
    left: number;
  } | null>(null);

  const resolvedIsMobile = isMobile || shellMode === "phone";
  const effectiveCollapsed = resolvedIsMobile ? false : collapsed;

  const handleNavigate = (url: string) => {
    router.push(url);
    if (resolvedIsMobile && onClose) onClose();
  };

  const showTip = (label: string, rect: DOMRect) => {
    if (!effectiveCollapsed) return;
    setTip({
      label,
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  };
  const hideTip = () => setTip(null);

  const handleCornerButtonClick = () => {
    if (resolvedIsMobile) {
      onClose?.();
      return;
    }
    setCollapsed((c) => !c);
    hideTip();
  };

  // Item tanpa privilege (null/undefined) = publik → selalu lolos filter.
  const visibleGroups = useMemo(
    () =>
      MENU.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.privilege === null ||
            item.privilege === undefined ||
            can(item.privilege, item.permission ?? PERMISSION.READ),
        ),
      })).filter((group) => group.items.length > 0),
    [can],
  );

  /* =======================================================================
     MODE TABLET -> BOTTOM DOCK (Liquid Glass)
     ======================================================================= */
  if (shellMode === "tablet") {
    return (
      <TabletDock
        groups={visibleGroups}
        loading={loading}
        pathname={pathname}
        onNavigate={handleNavigate}
      />
    );
  }

  return (
    <SidebarTipContext.Provider
      value={{ collapsed: effectiveCollapsed, show: showTip, hide: hideTip }}
    >
      {resolvedIsMobile && open && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      <aside
        className={`
        ${effectiveCollapsed ? "w-20" : "w-72 max-w-[85vw] md:max-w-none md:w-64 lg:w-72 xl:w-80"}
        ${resolvedIsMobile ? "fixed left-0 top-0 z-50 h-dvh" : "relative h-dvh"}
        ${resolvedIsMobile ? (open ? "translate-x-0" : "-translate-x-full") : ""}
        flex flex-col bg-gray-950 shadow-2xl transition-[width,transform] duration-300 ease-in-out
        `}
      >
        <button
          onClick={handleCornerButtonClick}
          aria-label={
            resolvedIsMobile
              ? "Tutup sidebar"
              : collapsed
                ? "Perlebar sidebar"
                : "Perkecil sidebar"
          }
          className="absolute -right-3 top-20 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-800 shadow-md transition-shadow hover:shadow-lg"
        >
          {resolvedIsMobile ? (
            <ChevronLeftCircle className="text-green-500" size={18} />
          ) : collapsed ? (
            <ChevronRightCircle className="text-green-500" size={20} />
          ) : (
            <ChevronLeftCircle className="text-green-500" size={20} />
          )}
        </button>

        {/* Header */}
        <div className="flex h-20 shrink-0 items-center gap-3 px-4 sm:px-5 lg:px-6">
          <div className="relative h-10 w-10 shrink-0">
            <Image
              src={SatkomlekLogo}
              alt="logo"
              fill
              className="object-contain"
            />
          </div>

          {!effectiveCollapsed && (
            <div className="min-w-0 leading-tight">
              <div className="truncate font-semibold text-gray-100">
                Satkomlek TNI
              </div>
              <div className="line-clamp-2 text-[11px] leading-snug tracking-wide text-gray-400 md:text-xs">
                e-Kodefikasi - Sistem Informasi Aset Terintegrasi (SIMAT)
              </div>
            </div>
          )}
        </div>

        {/* Menu */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-3 pb-[calc(env(safe-area-inset-bottom,0)+1.5rem)] pt-2 scrollbar-none [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {loading ? (
            <div className="space-y-2 px-1">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-11 w-full animate-pulse rounded-xl bg-gray-800/60"
                />
              ))}
            </div>
          ) : (
            visibleGroups.map((group) => (
              <Section
                key={group.title}
                title={group.title}
                collapsed={effectiveCollapsed}
              >
                {group.items.map((item) => (
                  <MenuItem
                    key={item.url}
                    collapsed={effectiveCollapsed}
                    label={item.label}
                    icon={item.icon}
                    active={pathname === item.url}
                    onClick={() => handleNavigate(item.url)}
                  />
                ))}
              </Section>
            ))
          )}
        </div>
      </aside>

      {/* Tooltip */}
      {effectiveCollapsed && tip && (
        <div
          className="pointer-events-none fixed z-60 -translate-y-1/2 whitespace-nowrap rounded-lg border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-100 shadow-xl"
          style={{ top: tip.top, left: tip.left }}
        >
          {tip.label}
          <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
        </div>
      )}
    </SidebarTipContext.Provider>
  );
}

function Section({
  title,
  children,
  collapsed,
}: {
  title: string;
  children: React.ReactNode;
  collapsed: boolean;
}) {
  return (
    <div className="mt-6 first:mt-0">
      {collapsed ? (
        <div className="mx-auto mb-2 h-px w-8 bg-gray-700 first:hidden" />
      ) : (
        <div className="mb-2 ml-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
          {title}
        </div>
      )}
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function MenuItem({
  label,
  icon: Icon,
  active,
  onClick,
  collapsed,
}: {
  label: string;
  icon: LucideIcon;
  active: boolean;
  onClick: () => void;
  collapsed: boolean;
}) {
  const ctx = useContext(SidebarTipContext);

  return (
    <div
      onClick={onClick}
      onMouseEnter={(e) =>
        collapsed && ctx?.show(label, e.currentTarget.getBoundingClientRect())
      }
      onMouseLeave={() => ctx?.hide()}
      aria-label={collapsed ? label : undefined}
      className={`
        flex items-center ${collapsed ? "justify-center" : "gap-3"}
        h-12 px-4 rounded-xl cursor-pointer transition-all duration-200 md:h-11
        ${
          active
            ? "bg-green-900/40 text-green-400 font-medium"
            : "text-gray-300 hover:bg-gray-800/60 hover:text-gray-100"
        }
      `}
    >
      <div className="flex w-6 justify-center">
        <Icon size={18} />
      </div>
      {!collapsed && <span className="text-sm font-medium">{label}</span>}
    </div>
  );
}

type FlatDockItem = MenuItemConfig & { groupIndex: number; groupTitle: string };

function TabletDock({
  groups,
  loading,
  pathname,
  onNavigate,
}: {
  groups: MenuGroup[];
  loading: boolean;
  pathname: string;
  onNavigate: (url: string) => void;
}) {
  const flatItems: FlatDockItem[] = useMemo(
    () =>
      groups.flatMap((group, groupIndex) =>
        group.items.map((item) => ({
          ...item,
          groupIndex,
          groupTitle: group.title,
        })),
      ),
    [groups],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [activeDot, setActiveDot] = useState(0);

  useEffect(() => {
    const activeItem = flatItems.find((it) => it.url === pathname);
    if (!activeItem) return;
    const node = itemRefs.current[activeItem.url];
    node?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [pathname, flatItems]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || groups.length <= 1) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const progress = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
    const dot = Math.min(
      groups.length - 1,
      Math.floor(progress * groups.length),
    );
    setActiveDot(dot);
  };

  return (
    <>
      {/* SVG Filter untuk efek refraksi (sama seperti Login) */}
      <svg
        className="pointer-events-none absolute h-0 w-0"
        aria-hidden="true"
        focusable="false"
      >
        <filter id="dock-glass-refraction" x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.012 0.012"
            numOctaves="1"
            seed="3"
            result="noise"
          />
          <feGaussianBlur in="noise" stdDeviation="2" result="blurredNoise" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="blurredNoise"
            scale="4"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </svg>

      <style jsx global>{`
        .dock-glass {
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.12),
            rgba(255, 255, 255, 0.03)
          );
          backdrop-filter: blur(28px) saturate(180%);
          -webkit-backdrop-filter: blur(28px) saturate(180%);
          filter: url(#dock-glass-refraction);
          -webkit-filter: url(#dock-glass-refraction);
          border: 1px solid rgba(255, 255, 255, 0.25);
          box-shadow:
            inset 0 1px 2px rgba(255, 255, 255, 0.7),
            inset 0 -2px 4px rgba(255, 255, 255, 0.1),
            inset 0 0 0 1px rgba(255, 255, 255, 0.05),
            0 12px 40px rgba(0, 0, 0, 0.5);
        }
        /* Lapisan kilau tambahan (seperti di Login) */
        .dock-glass::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0.25) 0%,
            transparent 40%,
            transparent 100%
          );
          pointer-events: none;
          border-radius: inherit;
        }
        /* Garis highlight atas (seperti Login) */
        .dock-glass::after {
          content: '';
          position: absolute;
          inset: 0;
          border-top: 1px solid rgba(255, 255, 255, 0.5);
          border-radius: inherit;
          pointer-events: none;
          mask: linear-gradient(to right, transparent 8%, black 30%, black 70%, transparent 92%);
          -webkit-mask: linear-gradient(to right, transparent 8%, black 30%, black 70%, transparent 92%);
        }

        .dock-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
        }
        .dock-scroll::-webkit-scrollbar {
          display: none;
        }
        .dock-scroll-mask {
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0,
            black 28px,
            black calc(100% - 28px),
            transparent 100%
          );
          mask-image: linear-gradient(
            to right,
            transparent 0,
            black 28px,
            black calc(100% - 28px),
            transparent 100%
          );
        }
        .dock-bar {
          height: clamp(4.25rem, 2.97rem + 2.68vw, 5.25rem);
          padding-inline: clamp(0.5rem, 0.2rem + 1vw, 1rem);
          border-radius: clamp(1.5rem, 1.1rem + 1.3vw, 2.25rem);
          position: relative; /* agar pseudo-element terbatas */
        }
        .dock-item {
          min-width: clamp(3.75rem, 2.5rem + 3.2vw, 4.75rem);
          gap: clamp(0.125rem, 0.05rem + 0.2vw, 0.25rem);
          padding-block: clamp(0.35rem, 0.2rem + 0.5vw, 0.6rem);
          border-radius: 0.75rem;
          transition: all 0.2s;
        }
        .dock-item-icon {
          width: clamp(1.375rem, 0.73rem + 1.34vw, 1.875rem);
          height: clamp(1.375rem, 0.73rem + 1.34vw, 1.875rem);
        }
        .dock-item-label {
          font-size: clamp(0.5625rem, 0.32rem + 0.5vw, 0.75rem);
          line-height: 1.15;
          font-weight: 500;
        }
        /* Active state dengan aksen biru (sesuai Login) */
        .dock-item-active {
          background: rgba(59, 130, 246, 0.2); /* biru transparan */
          color: #93c5fd; /* biru muda */
          box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.3);
        }
        .dock-item-inactive {
          color: rgba(255, 255, 255, 0.7);
        }
        .dock-item-inactive:hover {
          background: rgba(255, 255, 255, 0.08);
          color: white;
        }
      `}</style>

      <div
        className="fixed inset-x-0 z-40 flex justify-center px-3"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
      >
        {!loading && groups.length > 1 && (
          <div className="pointer-events-none absolute -top-4 left-1/2 flex -translate-x-1/2 gap-1.5">
            {groups.map((g, i) => (
              <span
                key={g.title}
                className={`h-1.5 w-1.5 rounded-full transition-colors ${
                  i === activeDot ? "bg-blue-300" : "bg-white/25"
                }`}
              />
            ))}
          </div>
        )}

        <div className="dock-glass dock-bar relative flex w-full max-w-3xl items-stretch overflow-hidden">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="dock-scroll dock-scroll-mask flex w-full snap-x snap-mandatory items-stretch gap-1 overflow-x-auto"
          >
            {loading ? (
              <div className="flex w-full items-center gap-3 px-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="dock-item flex shrink-0 animate-pulse flex-col items-center justify-center gap-1.5 rounded-2xl bg-white/10"
                  >
                    <div className="dock-item-icon rounded-full bg-white/15" />
                    <div className="h-2 w-8 rounded bg-white/15" />
                  </div>
                ))}
              </div>
            ) : (
              flatItems.map((item, idx) => {
                const isActive = pathname === item.url;
                const isNewGroup =
                  idx > 0 && flatItems[idx - 1].groupIndex !== item.groupIndex;
                const Icon = item.icon;

                return (
                  <div key={item.url} className="flex shrink-0 items-stretch">
                    {isNewGroup && (
                      <span className="mx-1 my-2 w-px shrink-0 self-stretch bg-white/12" />
                    )}
                    <button
                      ref={(node) => {
                        itemRefs.current[item.url] = node;
                      }}
                      onClick={() => onNavigate(item.url)}
                      className={`dock-item flex snap-center flex-col items-center justify-center rounded-2xl transition-all duration-200 ${
                        isActive
                          ? "dock-item-active"
                          : "dock-item-inactive"
                      }`}
                    >
                      <span className="dock-item-icon flex items-center justify-center">
                        <Icon
                          className="h-full w-full"
                          strokeWidth={isActive ? 2.5 : 2}
                        />
                      </span>
                      <span className="dock-item-label line-clamp-1 max-w-18 text-center">
                        {item.label}
                      </span>
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}